import React, { useState, useEffect } from 'react';
import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useNavigate } from 'react-router-dom';
import { PencilSimpleIcon, TrashSimpleIcon, PlusIcon } from '@phosphor-icons/react';
import { Button, Input, Modal, Pagination, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

interface NumerologyItem {
    id: number;
    key: string;
    number: number;
    title: string;
    description: string;
    created_at: string;
    updated_at: string;
}

const MyDailyReading: React.FC = () => {
    const [data, setData] = useState<NumerologyItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = sessionStorage.getItem('numerologyPage');
        return saved ? Number(saved) : 1;
    });
    const [totalItems, setTotalItems] = useState(0);
    const [countEnter, setCountEnter] = useState(0);
    const [countEnter2, setCountEnter2] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
    const [selectedNumber, setSelectedNumber] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (selectedNumber) sessionStorage.setItem('numberNumerology', selectedNumber);
        if (searchText) sessionStorage.setItem('searchNumerology', searchText);
        if (selectedType) sessionStorage.setItem('typeNumerology', selectedType);
        setCountEnter2(prev => prev + 1);
    }, [searchText, selectedNumber, selectedType]);

    useEffect(() => {
        if (countEnter > 0) fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType, selectedNumber, searchText]);

    const clickPagination = (key: number) => {
        setCurrentPage(key);
        sessionStorage.setItem('numerologyPage', JSON.stringify(key));
    };

    useEffect(() => {
        const saved = sessionStorage.getItem('numerologyPage');
        if (saved) setCurrentPage(Number(saved));
        else setCurrentPage(1);
        fetchData(currentPage);
    }, []);

    useEffect(() => {
        const searchKey = sessionStorage.getItem('searchNumerology');
        const numberKey = sessionStorage.getItem('numberNumerology');
        const typeKey = sessionStorage.getItem('typeNumerology');
        if (searchKey || numberKey || typeKey) {
            sessionStorage.removeItem('searchNumerology');
            sessionStorage.removeItem('numberNumerology');
            sessionStorage.removeItem('typeNumerology');
            if (countEnter2 > 0) { setCountEnter2(prev => prev - 1); fetchData(currentPage); }
            else fetchData(1);
        } else {
            if (typeof currentPage === 'number') fetchData(currentPage);
        }
        setCountEnter(prev => prev + 1);
    }, [currentPage]);

    const fetchData = async (page = 1, perPage = 10) => {
        try {
            setLoading(true);
            let url = `${API_URL}api/v1/be/numerology_indices?page=${page}&limit=${perPage}`;
            if (selectedType) url += `&key=${encodeURIComponent(selectedType)}`;
            if (selectedNumber) url += `&number=${encodeURIComponent(selectedNumber)}`;
            if (searchText.trim()) url += `&title=${encodeURIComponent(searchText.trim())}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const json = await response.json();
            setData(json.data?.items || []);
            setCurrentPage(json.data?.current_page || 1);
            setTotalItems(json.data?.total || 0);
        } catch (err) {
            console.error("Lỗi khi gọi API:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: "Xác nhận xóa",
            content: "Bạn có chắc muốn xóa mục này không?",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await fetch(`${API_URL}api/v1/be/numerology_indices/${id}`, {
                        method: "DELETE",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                    const json = await response.json();
                    if (json.status) {
                        Modal.success({ title: "Thành công", content: "Xóa thành công!" });
                        await fetchData(currentPage);
                    } else {
                        Modal.error({ title: "Thất bại", content: "Xóa thất bại: " + (json.message || "Unknown error") });
                    }
                } catch (error: unknown) {
                    Modal.error({ title: "Lỗi", content: "Lỗi khi xóa: " + (error instanceof Error ? error.message : String(error)) });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const columns: ColumnsType<NumerologyItem> = [
        {
            title: 'Name',
            key: 'name',
            render: (_, item) => {
                try {
                    const title = JSON.parse(item.title);
                    return <span className="text-sm font-medium">{title.vi || title.en}</span>;
                } catch {
                    return <span className="text-sm font-medium">{item.title}</span>;
                }
            },
        },
        {
            title: 'Type',
            dataIndex: 'key',
            render: (val: string) => <Tag className="text-xs">{val}</Tag>,
        },
        {
            title: 'Number',
            dataIndex: 'number',
            width: 90,
            render: (val: number) => <span className="text-sm">{val}</span>,
        },
        {
            title: 'Action',
            key: 'actions',
            width: 90,
            render: (_, item) => (
                <div className="flex gap-1.5">
                    <button
                        onClick={() => navigate(`/numerology/edit/${item.id}`)}
                        className="p-1.5 rounded hover:bg-bg-hover transition-colors text-text-secondary hover:text-blue-500 cursor-pointer"
                        title="Edit"
                    >
                        <PencilSimpleIcon size={15} />
                    </button>
                    <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors text-text-secondary hover:text-red-500 cursor-pointer"
                        title="Delete"
                    >
                        <TrashSimpleIcon size={15} />
                    </button>
                </div>
            ),
        },
    ];


    return (
        <SidebarLayout
            title={<h4 className="text-2xl font-bold m-0! py-4">My Numerology</h4>}
        >
            <div className="space-y-3">
                <Input.Search
                    placeholder="Search..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    className="w-full"
                />

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <Select
                            placeholder="Filter by Type"
                            className="w-52"
                            allowClear
                            value={selectedType}
                            onChange={setSelectedType}
                            options={[
                                { value: 'alignment_signals', label: 'alignment_signals' },
                                { value: 'balance', label: 'balance' },
                                { value: 'emotional_response_style', label: 'emotional_response_style' },
                                { value: 'life_path', label: 'life_path' },
                                { value: 'life_purpose', label: 'life_purpose' },
                                { value: 'link_connection', label: 'link_connection' },
                                { value: 'maturity', label: 'maturity' },
                                { value: 'milestone_1', label: 'milestone_1' },
                                { value: 'milestone_2', label: 'milestone_2' },
                                { value: 'milestone_3', label: 'milestone_3' },
                                { value: 'milestone_4', label: 'milestone_4' },
                                { value: 'missing_aspects', label: 'missing_aspects' },
                                { value: 'passion', label: 'passion' },
                                { value: 'personality', label: 'personality' },
                                { value: 'rational_thinking', label: 'rational_thinking' },
                                { value: 'societal_adaptability_index', label: 'societal_adaptability_index' },
                                { value: 'soul', label: 'soul' },
                                { value: 'soul_personality_link', label: 'soul_personality_link' },
                            ]}
                        />
                        <Select
                            placeholder="Filter by Number"
                            className="w-40"
                            allowClear
                            value={selectedNumber}
                            onChange={setSelectedNumber}
                            options={[1,2,3,4,5,6,7,8,9].map(n => ({ value: n.toString(), label: n }))}
                        />
                        {(selectedType || selectedNumber || searchText) && (
                            <Button onClick={() => { setSearchText(''); setSelectedType(undefined); setSelectedNumber(undefined); }}>
                                Clear Filters
                            </Button>
                        )}
                    </div>
                    <Button type="primary" icon={<PlusIcon size={14} />} onClick={() => navigate('/add-numerology')}>
                        Add
                    </Button>
                </div>

                <div className="bg-bg-card border border-border rounded-sm shadow-card">
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        locale={{ emptyText: <div className="py-10 text-center text-text-muted text-sm">No data found</div> }}
                    />
                    <div className="flex justify-end px-5 py-3 border-t border-border">
                        <Pagination
                            current={currentPage}
                            total={totalItems}
                            pageSize={10}
                            showSizeChanger={false}
                            showTotal={total => `Total ${total} items`}
                            onChange={clickPagination}
                        />
                    </div>
                </div>
            </div>
        </SidebarLayout>
    );
};

export default MyDailyReading;
